import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Filter, Search, SearchX, Signal } from 'lucide-react';
import { toast } from 'sonner';
import {
  AnalyticsComparisonWindow,
  AnalyticsDiscoverySignalRow,
  AnalyticsDiscoverySummary,
  AnalyticsOverview,
  buildDiscoverySignalRows,
  compareMetricValues,
  hasDiscoveryContent,
} from '@/services/analyticsMetrics';
import {
  FirstPartyAnalyticsRange,
  getDiscoveryAnalytics,
  getFirstPartyAnalyticsOverviewComparison,
} from '@/services/analyticsService';
import {
  AnalyticsEmptyState,
  AnalyticsErrorState,
  AnalyticsLoadingState,
  AnalyticsPageHeader,
  AnalyticsRefreshControl,
  ChartCard,
  MetricCard,
} from '@/components/analytics/AnalyticsShared';
import { useAnalyticsAutoRefresh } from '@/hooks/useAnalyticsAutoRefresh';
import { ANALYTICS_COLORS, formatDeltaText, formatNumber, formatPercent, formatUtcRange } from '@/components/analytics/analyticsDisplay';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/i18n';

export default function DiscoveryAnalytics() {
  const [range, setRange] = useState<FirstPartyAnalyticsRange>('7d');
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [previousOverview, setPreviousOverview] = useState<AnalyticsOverview | null>(null);
  const [comparisonWindow, setComparisonWindow] = useState<AnalyticsComparisonWindow | null>(null);
  const [discovery, setDiscovery] = useState<AnalyticsDiscoverySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  const loadAnalytics = useCallback(async (silent = false) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const [overviewResult, discoveryResult] = await Promise.all([
        getFirstPartyAnalyticsOverviewComparison(range),
        getDiscoveryAnalytics(range),
      ]);

      setOverview(overviewResult.current);
      setPreviousOverview(overviewResult.previous);
      setComparisonWindow(overviewResult.window);
      setDiscovery(discoveryResult);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('analyticsLoadFailed');
      setError(message);
      if (!silent) toast.error(message);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const silentRefresh = useCallback(() => loadAnalytics(true), [loadAnalytics]);
  useAnalyticsAutoRefresh(silentRefresh);

  const hasDiscoveryRows = hasDiscoveryContent(discovery);

  const topSearch = discovery?.topSearches[0];
  const topMissedDemand = discovery?.missedDemand[0];
  const searchChartRows = useMemo(() => discovery?.topSearches.slice(0, 10) || [], [discovery]);
  const filterChartRows = useMemo(() => discovery?.filterUsage.slice(0, 10) || [], [discovery]);
  const signalRows = useMemo(() => discovery ? buildDiscoverySignalRows(discovery, 10) : [], [discovery]);
  const comparisonLabel = t('analyticsComparedWithPrior');

  return (
    <div className="space-y-6 animate-fade-in">
      <AnalyticsPageHeader
        title={t('analyticsDiscoveryTitle')}
        description={t('analyticsDiscoveryDescription')}
        range={range}
        onRangeChange={setRange}
        rangeLabel={t('analyticsDateRange')}
      >
        {overview?.start && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('analyticsUtcRange', { range: formatUtcRange(overview.start, overview.end) })}
          </p>
        )}
        {comparisonWindow?.previous && (
          <p className="mt-1 text-xs text-muted-foreground">
            {t('analyticsPriorUtcRange', { range: formatUtcRange(comparisonWindow.previous.start, comparisonWindow.previous.end) })}
          </p>
        )}
        <AnalyticsRefreshControl
          refreshLabel={t('analyticsRefresh')}
          hintLabel={t('analyticsAutoRefreshHint')}
          refreshing={refreshing}
          disabled={loading}
          onRefresh={silentRefresh}
        />
      </AnalyticsPageHeader>

      {loading ? (
        <AnalyticsLoadingState label={t('analyticsLoading')} />
      ) : error ? (
        <AnalyticsErrorState
          title={t('analyticsErrorTitle')}
          message={error}
          retryLabel={t('analyticsRetry')}
          onRetry={() => loadAnalytics()}
        />
      ) : !hasDiscoveryRows ? (
        <AnalyticsEmptyState title={t('analyticsNoEventsTitle')} description={t('analyticsNoDiscoveryEventsDescription')} />
      ) : discovery ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title={t('analyticsSearches')}
              value={formatNumber(overview?.searches || 0)}
              description={t('analyticsSearchesDescription')}
              icon={Search}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview?.searches, previousOverview?.searches), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsZeroResultRate')}
              value={formatPercent(overview?.zeroResultRate ?? null)}
              description={t('analyticsZeroResultRateDescription')}
              icon={SearchX}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview?.zeroResultRate, previousOverview?.zeroResultRate), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsTopSearch')}
              value={topSearch?.dimension || '--'}
              description={t('analyticsTopSearchDescription')}
              icon={Signal}
            />
            <MetricCard
              title={t('analyticsTopMissedDemand')}
              value={topMissedDemand?.dimension || '--'}
              description={t('analyticsTopMissedDemandDescription')}
              icon={Filter}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <ChartCard title={t('analyticsTopSearches')} description={t('analyticsTopSearchesDescription')}>
              {searchChartRows.length > 0 ? (
                <div className="h-[320px]" role="img" aria-label={t('analyticsTopSearches')}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={searchChartRows} layout="vertical" margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="dimension"
                        width={120}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => String(value).slice(0, 18)}
                      />
                      <Tooltip formatter={(value: number) => [formatNumber(value), t('analyticsSearches')]} />
                      <Bar dataKey="count" name={t('analyticsSearches')} fill={ANALYTICS_COLORS[0]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <AnalyticsEmptyState title={t('analyticsNoSearchesTitle')} description={t('analyticsNoSearchesDescription')} />
              )}
            </ChartCard>

            <ChartCard title={t('analyticsFilterUsage')} description={t('analyticsFilterUsageDescription')}>
              {filterChartRows.length > 0 ? (
                <div className="h-[320px]" role="img" aria-label={t('analyticsFilterUsage')}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={filterChartRows} margin={{ top: 10, right: 20, bottom: 40, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="dimension" angle={-30} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value: number) => [formatNumber(value), t('analyticsFilterEvents')]} />
                      <Bar dataKey="count" name={t('analyticsFilterEvents')} fill={ANALYTICS_COLORS[1]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <AnalyticsEmptyState title={t('analyticsNoFiltersTitle')} description={t('analyticsNoFiltersDescription')} />
              )}
            </ChartCard>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <ChartCard title={t('analyticsMissedDemand')} description={t('analyticsMissedDemandDescription')}>
              <RankingTable rows={discovery.missedDemand} label={t('analyticsSearchTerm')} countLabel={t('analyticsZeroResultSearches')} />
            </ChartCard>
            <ChartCard title={t('analyticsFeedAndErrors')} description={t('analyticsFeedAndErrorsDescription')}>
              <RankingTable
                rows={signalRows.map((row) => ({
                  key: row.key,
                  dimension: formatSignalLabel(row, t),
                  count: row.count,
                }))}
                label={t('analyticsSignal')}
                countLabel={t('analyticsEvents')}
              />
            </ChartCard>
          </div>
        </>
      ) : null}
    </div>
  );
}

function RankingTable({
  rows,
  label,
  countLabel,
}: {
  rows: { key?: string; dimension: string; count: number }[];
  label: string;
  countLabel: string;
}) {
  const { t } = useLanguage();

  if (rows.length === 0) {
    return <AnalyticsEmptyState title={t('analyticsNoRowsTitle')} description={t('analyticsNoRowsDescription')} />;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{label}</TableHead>
            <TableHead className="text-right">{countLabel}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key || `${label}-${row.dimension}`}>
              <TableCell className="max-w-[280px] truncate font-medium">{row.dimension}</TableCell>
              <TableCell className="text-right">{formatNumber(row.count)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function formatSignalLabel(row: AnalyticsDiscoverySignalRow, t: (key: string) => string) {
  const prefix = row.source === 'feed_failure'
    ? t('analyticsFeedFailurePrefix')
    : t('analyticsClientErrorPrefix');

  return `${prefix}: ${row.dimension}`;
}
